import React, { useMemo } from 'react';
import { Card, CardContent } from './ui/card';
import { DollarSign, TrendingUp, Users, Calculator } from 'lucide-react';

/**
 * LiveRateCalculator
 * Real-time calc of client total, talent payout, and margin based on
 * bill_rate, talent_pay_rate, hours, and staff_count.
 *
 * Admin-only view (never rendered for talent/client).
 */
const fmt = (n) => `$${(Number(n) || 0).toFixed(2)}`;

const LiveRateCalculator = ({ billRate, talentPayRate, hours, staffCount, flatPay }) => {
  const data = useMemo(() => {
    const b = parseFloat(billRate) || 0;
    const p = parseFloat(talentPayRate) || 0;
    const h = parseFloat(hours) || 0;
    const s = parseInt(staffCount, 10) || 0;
    const flat = parseFloat(flatPay) || 0;
    const isFlat = flat > 0;
    const clientTotal = b * h * s;
    // Flat gigs pay a fixed total per talent regardless of hours.
    const talentTotal = isFlat ? flat * s : p * h * s;
    const marginTotal = clientTotal - talentTotal;
    const marginPct = clientTotal > 0 ? (marginTotal / clientTotal) * 100 : 0;
    const marginPerHr = Math.max(0, b - p);
    return { b, p, h, s, flat, isFlat, marginPerHr, marginPct, clientTotal, talentTotal, marginTotal };
  }, [billRate, talentPayRate, hours, staffCount, flatPay]);

  const marginOk = data.marginPct >= 20 && data.clientTotal > 0 && data.talentTotal > 0;
  const marginLow = data.marginPct > 0 && data.marginPct < 20 && data.clientTotal > 0 && data.talentTotal > 0;
  const ratesInverted = !data.isFlat && data.p > 0 && data.b > 0 && data.p > data.b;

  return (
    <Card className="bg-gradient-to-br from-cyan-50 to-blue-50 border-cyan-200" data-testid="live-rate-calculator">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2 text-sm font-semibold text-cyan-900">
          <Calculator className="w-4 h-4" />
          Live Rate Calculator
          <span className="text-xs font-normal text-cyan-700 ml-auto">Admin-only view</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
          <div className="p-3 rounded-lg bg-white border border-cyan-100">
            <div className="text-[11px] text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <DollarSign className="w-3 h-3" /> Client Total
            </div>
            <div className="text-xl font-bold text-blue-700 mt-0.5" data-testid="calc-client-total">
              {fmt(data.clientTotal)}
            </div>
            <div className="text-[11px] text-gray-500">
              {fmt(data.b)}/hr × {data.h}h × {data.s} staff
            </div>
          </div>

          <div className="p-3 rounded-lg bg-white border border-cyan-100">
            <div className="text-[11px] text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <Users className="w-3 h-3" /> Talent Payout
            </div>
            <div className="text-xl font-bold text-emerald-700 mt-0.5" data-testid="calc-talent-total">
              {fmt(data.talentTotal)}
            </div>
            <div className="text-[11px] text-gray-500">
              {data.isFlat ? `${fmt(data.flat)} flat × ${data.s} staff` : `${fmt(data.p)}/hr × ${data.h}h × ${data.s} staff`}
            </div>
          </div>

          <div className={`p-3 rounded-lg bg-white border ${marginOk ? 'border-green-200' : marginLow ? 'border-amber-200' : 'border-cyan-100'}`}>
            <div className="text-[11px] text-gray-500 uppercase tracking-wide flex items-center gap-1">
              <TrendingUp className="w-3 h-3" /> Margin
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xl font-bold text-gray-900" data-testid="calc-margin-total">
                {fmt(data.marginTotal)}
              </span>
              <span className={`text-sm font-semibold ${marginOk ? 'text-green-700' : marginLow ? 'text-amber-700' : 'text-gray-500'}`} data-testid="calc-margin-pct">
                {data.marginPct.toFixed(1)}%
              </span>
            </div>
            <div className="text-[11px] text-gray-500">
              {data.isFlat ? 'flat rate' : `${fmt(data.marginPerHr)}/hr per staff`}
            </div>
          </div>
        </div>

        {ratesInverted && (
          <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2" data-testid="calc-warning">
            Pay rate cannot exceed bill rate — fix before saving.
          </div>
        )}
        {marginLow && !ratesInverted && (
          <div className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded p-2">
            Margin below 20%. Double-check pricing.
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default LiveRateCalculator;
